import { cn } from '@/lib/utils';
import type { TrustGraphViewStats } from '@uncain/shared';
import { Search, Sparkles } from 'lucide-react';

interface TrustGraphToolbarProps {
	readonly metrics: TrustGraphViewStats['metrics'];
	readonly searchTerm: string;
	readonly onSearchChange: (value: string) => void;
	readonly showHiddenNodes: boolean;
	readonly onToggleHiddenNodes: () => void;
	readonly riskOnly: boolean;
	readonly onToggleRiskOnly: () => void;
	readonly compact?: boolean;
}

export function TrustGraphToolbar({
	metrics,
	searchTerm,
	onSearchChange,
	showHiddenNodes,
	onToggleHiddenNodes,
	riskOnly,
	onToggleRiskOnly,
	compact = false,
}: TrustGraphToolbarProps) {
	return (
		<div className="space-y-4 rounded-2xl border border-border/70 bg-muted/30 p-4">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
				<label className="relative block min-w-0 flex-1">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<input
						type="search"
						value={searchTerm}
						onChange={(event) => onSearchChange(event.target.value)}
						placeholder="Search nodes, repos, workflows, or evidence"
						className="w-full rounded-xl border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
					/>
				</label>
				<div className="flex flex-wrap gap-2">
					<ToggleChip active={showHiddenNodes} onClick={onToggleHiddenNodes}>
						Show hidden layers
					</ToggleChip>
					<ToggleChip active={riskOnly} onClick={onToggleRiskOnly}>
						<Sparkles className="h-3.5 w-3.5" />
						Risky paths only
					</ToggleChip>
				</div>
			</div>
			{!compact && (
				<div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
					{metrics.map((metric) => (
						<div
							key={metric.label}
							className="rounded-xl border border-border bg-background px-3 py-2"
						>
							<div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
								{metric.label}
							</div>
							<div
								className={cn(
									'mt-1 text-xl font-semibold text-foreground',
									metric.tone === 'warning' && 'text-severity-high',
									metric.tone === 'success' && 'text-severity-low',
									metric.tone === 'info' && 'text-primary',
								)}
							>
								{metric.value}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function ToggleChip({
	active,
	onClick,
	children,
}: {
	readonly active: boolean;
	readonly onClick: () => void;
	readonly children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={cn(
				'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
				active
					? 'border-primary/30 bg-primary/10 text-primary'
					: 'border-border bg-background text-muted-foreground hover:border-primary/20 hover:text-foreground',
			)}
		>
			{children}
		</button>
	);
}
